import React from 'react';
import { Box, Link, Typography, Button } from '@mui/material';
import { Formik, Form } from 'formik';
import { SignInData } from 'Types';
import SignInForm, { signInFormSchema } from 'Components/Forms/SignInForm';
import { ValidateForm } from 'Utils/Forms';
// import { Auth } from 'aws-amplify';
import { MUSEFLOW_BLUE, MUSEFLOW_OFF_WHITE_1, MUSEFLOW_ORANGE } from 'Components/Colors';
import { isPhoneNumber, isEmail } from 'Components/Forms/SignUpForm';
import { useNavigate } from 'react-router-dom';
import { hasAuthParams } from 'react-oidc-context';
import {
  EMAIL_LINK_RESENT_MESSAGE,
  SetErrorsPayload,
  useAuthWrapperContext,
} from 'Contexts/AuthWrapperContext';
import { usePressedState } from 'Utils/usePressedState';
import SocialSignInButtons from 'Components/Forms/SocialSignInButtons';
import { AuthPendingOverlay, OrDivider, TermsAndPrivacyLine } from 'Components/Forms/AuthPageParts';
import { useEmailOrPhonePrefill } from 'Components/Forms/useEmailOrPhonePrefill';
import { getLastPhoneCountry } from 'Utils/LastPhoneCountry';

type SignInProps = {
  forgotPasswordNavFunction: () => void;
  goBackFn: () => void;
};

const SignIn: React.FC<SignInProps> = (props) => {
  const [_isIpad, _setIsIpad] = React.useState(false);
  const _navigate = useNavigate();
  const { forgotPasswordNavFunction } = props;
  const {
    pendingEmailOrPhone,
    errors,
    logInUsername,
    resendSignUpUsername,
    signUpCodeResent,
    resetErrors,
    isLoading,
    oauthPending,
    tempSignUpPass,
    setPendingEmailOrPhone,
  } = useAuthWrapperContext();
  // Pressed states for the plain links below (see usePressedState).
  const blockerResendPress = usePressedState();
  const otherEmailPress = usePressedState();
  const pendingResendPress = usePressedState();

  // Only show spinner if path doesn't include 'logout' (logout has auth params but shouldn't show spinner)
  const isOAuthLoginCallback = hasAuthParams();

  const [formErrors, setFormErrors] = React.useState<SetErrorsPayload | object>({});

  // An email or phone number carried over from the sign-up page or the link
  // that opened this page.
  const emailOrPhonePrefill = useEmailOrPhonePrefill();

  // A phone sign-in starts on the country last used for one on this device.
  const initialCountryAcronym = React.useMemo(() => getLastPhoneCountry() ?? 'US', []);

  React.useEffect(() => {
    console.log('setting form errors:', errors);
    setFormErrors(errors);
  }, [errors]);

  const showEmailVerificationBlocker = Boolean(
    pendingEmailOrPhone && isEmail(pendingEmailOrPhone) && tempSignUpPass,
  );

  console.log('window.location.origin:', window.location.origin);

  return (
    <>
      {console.log('isLoading:', isLoading)}
      {(isLoading || oauthPending || isOAuthLoginCallback) && <AuthPendingOverlay />}

      <h3 className="title-welcome" style={{ marginTop: '50px' }}>
        Welcome to MuseFlow!
      </h3>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        {/* <Box sx={{...textStyles,color: MUSEFLOW_OFF_WHITE_1,marginBottom: '10px'}}>isCapacitorNative(): {isCapacitorNative() ? "Yes" : "No"}</Box>
      <Box sx={{...textStyles,color: MUSEFLOW_OFF_WHITE_1,marginBottom: '10px'}}>isIosTablet(): {isIosTablet() ? "Yes" : "No"}</Box>
      <Box sx={{...textStyles,color: MUSEFLOW_OFF_WHITE_1,marginBottom: '10px'}}>window.innerWidth: {window.innerWidth}</Box>
      <Box sx={{...textStyles,color: MUSEFLOW_OFF_WHITE_1,marginBottom: '10px'}}>window.innerHeight: {window.innerHeight}</Box>
      <Box sx={{...textStyles,color: MUSEFLOW_OFF_WHITE_1,marginBottom: '10px'}}>window.agent: {window.navigator.userAgent}</Box>
      <Box sx={{...textStyles,color: MUSEFLOW_OFF_WHITE_1,marginBottom: '10px'}}>{"window.innerWidth >= 1000 && window.innerHeight < 1000"}: {(window.innerWidth >= 1000 && window.innerHeight < 1000) ? "yes" : "no"}</Box>
      <Box sx={{...textStyles,color: MUSEFLOW_OFF_WHITE_1,marginBottom: '10px'}}>Is an ipad: {isIosTablet() && !isCapacitorNative() && ((window.innerWidth >= 1100 && window.innerHeight < 1100) || (window.innerWidth < 1100 && window.innerHeight >= 1100)) ? "Yes" : "No"}</Box> */}

        <Box
          sx={{
            // width:'80%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <SocialSignInButtons />
          <OrDivider />

          {showEmailVerificationBlocker && (
            <Box
              sx={{
                width: { xs: 'min(83vw, 468px)', sm: '350px' },
                maxWidth: { xs: 'min(83vw, 468px)', sm: '350px' },
                paddingBottom: '20px',
              }}
            >
              <Typography
                sx={{
                  marginBottom: '16px',
                  padding: '12px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(57, 94, 227, 0.40)',
                }}
                color="#00C2FF"
              >
                Verify your email to continue.
                <br></br>
                Click the verification link, then return here.
              </Typography>

              {errors.unknown && (
                <Typography sx={{ marginBottom: '10px' }} color="error.main">
                  {errors.unknown}
                </Typography>
              )}

              <Button
                sx={{
                  width: '100%',
                  marginBottom: '20px',
                  marginTop: '8px',
                  fontSize: '16px',
                  color: '#050B13',
                  background: MUSEFLOW_ORANGE,
                  boxShadow:
                    '0px 3.61149px 1.20383px -2.40766px rgba(0, 0, 0, 0.2), 0px 2.40766px 2.40766px rgba(0, 0, 0, 0.14), 0px 1.20383px 6.01915px rgba(0, 0, 0, 0.12)',
                  borderRadius: '40px',
                  padding: '14px 28px',
                  fontFamily: 'Lato',
                  fontStyle: 'normal',
                  letterSpacing: '2.0px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  '&:hover': {
                    background: MUSEFLOW_ORANGE,
                  },
                }}
                onClick={async () => {
                  if (!pendingEmailOrPhone || !tempSignUpPass) return;
                  resetErrors();
                  await logInUsername(pendingEmailOrPhone, tempSignUpPass, '');
                }}
              >
                I&apos;ve verified, continue
              </Button>

              <Link
                sx={{
                  display: 'block',
                  textAlign: 'center' as const,
                  cursor: 'pointer',
                  marginBottom: '10px',
                  color: MUSEFLOW_BLUE,
                  ...blockerResendPress.pressedSx,
                }}
                {...blockerResendPress.pressHandlers}
                onClick={() => {
                  resendSignUpUsername(pendingEmailOrPhone || '');
                }}
              >
                Didn&apos;t get a link? Click here to resend.
              </Link>
              {signUpCodeResent && (
                <Typography
                  role="status"
                  color="#00C2FF"
                  sx={{ textAlign: 'center', marginBottom: '10px' }}
                >
                  {EMAIL_LINK_RESENT_MESSAGE}
                </Typography>
              )}

              <Link
                sx={{
                  display: 'block',
                  textAlign: 'center' as const,
                  cursor: 'pointer',
                  color: MUSEFLOW_BLUE,
                  ...otherEmailPress.pressedSx,
                }}
                {...otherEmailPress.pressHandlers}
                onClick={() => {
                  resetErrors();
                  setPendingEmailOrPhone?.(undefined);
                }}
              >
                Use a different email
              </Link>
            </Box>
          )}

          {!showEmailVerificationBlocker && (
            <>
              {pendingEmailOrPhone && isEmail(pendingEmailOrPhone) && (
                <Box sx={{ paddingBottom: '20px' }}>
                  <Typography
                    sx={{
                      marginBottom: '20px',
                      padding: '10px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(57, 94, 227, 0.40)',
                    }}
                    color="#00C2FF"
                  >
                    Your registration is pending. Check your email to verify.
                    <br></br>
                    (If you don&apos;t see an email, check your spam folder)
                  </Typography>
                  <Link
                    sx={{
                      textAlign: 'center' as const,
                      cursor: 'pointer',
                      marginBottom: '10px',
                      color: MUSEFLOW_BLUE,
                      ...pendingResendPress.pressedSx,
                    }}
                    {...pendingResendPress.pressHandlers}
                    onClick={() => {
                      resendSignUpUsername(pendingEmailOrPhone || '');
                    }}
                  >
                    Didn&apos;t get a link? Click here to resend.
                  </Link>
                  {signUpCodeResent && (
                    <Typography role="status" color="#00C2FF" sx={{ marginTop: '10px' }}>
                      {EMAIL_LINK_RESENT_MESSAGE}
                    </Typography>
                  )}
                </Box>
              )}

              {pendingEmailOrPhone && isPhoneNumber(pendingEmailOrPhone) && (
                <Typography
                  sx={{
                    marginBottom: '20px',
                    padding: '10px',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(57, 94, 227, 0.40)',
                  }}
                  color="#00C2FF"
                >
                  Your verification is pending.<br></br>Check your text messages for a verification
                  code.
                </Typography>
              )}

              <Formik
                initialValues={{
                  emailOrPhone:
                    pendingEmailOrPhone && isEmail(pendingEmailOrPhone)
                      ? pendingEmailOrPhone
                      : emailOrPhonePrefill,
                  password: '',
                  unknown: '',
                  countryAcronym: initialCountryAcronym,
                }}
                validate={(values: SignInData) => {
                  return ValidateForm(signInFormSchema, values);
                }}
                onSubmit={async (values, _formikHelpers) => {
                  await logInUsername(values.emailOrPhone, values.password, values.countryAcronym);
                }}
                validateOnChange={false}
              >
                {(formikProps) => (
                  <Form
                    autoComplete="on"
                    onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                      e.preventDefault();
                      resetErrors();
                      formikProps.handleSubmit(e);
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 'min(83vw, 468px)', sm: '350px' },
                        maxWidth: { xs: 'min(83vw, 468px)', sm: '350px' },
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <SignInForm
                        forgotPasswordNavFunction={forgotPasswordNavFunction}
                        {...formikProps}
                        errors={{ ...formErrors, ...formikProps.errors }}
                      />
                    </Box>
                  </Form>
                )}
              </Formik>
            </>
          )}
          <TermsAndPrivacyLine />
          <Box
            sx={{
              color: MUSEFLOW_OFF_WHITE_1,
              marginTop: '20px',
              marginBottom: '24px',
              ...textStyles,
            }}
          >
            New to MuseFlow? &nbsp;{' '}
            <Link href={'/register'} sx={{ color: '#6F87F1' }}>
              {' '}
              Create an account
            </Link>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const textStyles = {
  fontFamily: 'Lato',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
  letterSpacing: '0.16px',
};

export default SignIn;
